import type {
  InstalledTemplateInfo,
  MarketplaceTemplate,
} from './use-marketplace-api';

export type InstalledTemplatePrimaryAction =
  | 'open_doc'
  | 'open_workflow'
  | 'use_step'
  | 'open_apps'
  | 'none';

export interface InstalledTemplateManagement {
  isInstalled: boolean;
  isPreinstalled: boolean;
  templateType: string;
  primaryAction: InstalledTemplatePrimaryAction;
  primaryDocId: string | null;
  workflowId: string | null;
  canOpenDoc: boolean;
  canOpenWorkflow: boolean;
  canUseStep: boolean;
  canRemove: boolean;
  removeMode: 'detach_install_record' | 'preinstalled' | 'unavailable';
}

export function resolveInstalledTemplateManagement(
  template: MarketplaceTemplate,
  installedInfo?: InstalledTemplateInfo
): InstalledTemplateManagement {
  const isPreinstalled = template.isPreinstalled === true;
  const legacyInstalled =
    template.installedVersionNumber != null &&
    template.installedVersionNumber > 0;
  const isInstalled =
    isPreinstalled || installedInfo != null || legacyInstalled;
  const templateType =
    installedInfo?.templateType ?? template.templateType ?? 'workflow';

  if (!isInstalled) {
    return emptyManagement(templateType, false);
  }

  const backendActions = installedInfo?.managementActions;
  const primaryDocId =
    installedInfo?.artifactSummary?.primaryDocId ??
    installedInfo?.installedDocIds?.[0] ??
    null;
  const workflowId =
    installedInfo?.artifactSummary?.workflowId ??
    installedInfo?.workflowId ??
    null;

  const canOpenDoc =
    backendActions?.canOpenDoc ??
    ((templateType === 'automation' ||
      templateType === 'app' ||
      templateType === 'doc') &&
      !!primaryDocId);
  const canOpenWorkflow =
    backendActions?.canOpenWorkflow ??
    ((templateType === 'automation' || templateType === 'workflow') &&
      !!workflowId);
  const canUseStep = backendActions?.canUseStep ?? templateType === 'step';
  const backendPrimaryAction = backendActions?.primaryAction;
  const fallbackPrimaryAction = canOpenDoc
    ? 'open_doc'
    : canOpenWorkflow
      ? 'open_workflow'
      : canUseStep
        ? 'use_step'
        : 'none';
  const primaryAction =
    templateType === 'app_block' &&
    (backendPrimaryAction == null || backendPrimaryAction === 'none')
      ? 'open_apps'
      : (backendPrimaryAction ?? fallbackPrimaryAction);

  return {
    isInstalled,
    isPreinstalled,
    templateType,
    primaryAction,
    primaryDocId,
    workflowId,
    canOpenDoc,
    canOpenWorkflow,
    canUseStep,
    canRemove:
      !isPreinstalled &&
      installedInfo?.managementActions?.canRemove !== false &&
      !!installedInfo,
    removeMode: isPreinstalled
      ? 'preinstalled'
      : installedInfo
        ? 'detach_install_record'
        : 'unavailable',
  };
}

function emptyManagement(
  templateType: string,
  isPreinstalled: boolean
): InstalledTemplateManagement {
  return {
    isInstalled: false,
    isPreinstalled,
    templateType,
    primaryAction: 'none',
    primaryDocId: null,
    workflowId: null,
    canOpenDoc: false,
    canOpenWorkflow: false,
    canUseStep: false,
    canRemove: false,
    removeMode: 'unavailable',
  };
}
